import { defaultStringifySearch, useBlocker, useRouter, useSearch } from '@tanstack/react-router';
import { FilePlus2 } from 'lucide-react';
import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { TEMPLATE_AUDIENCES, templateAudienceOf, type TemplateKind, type TemplateRow, type TemplateVersionRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Popover } from '../../../components/Popover';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanEdit } from '../../../stores/session';
import { useAdminTemplates, useTemplateVersions } from '../api/communication-api';
import { TemplateList } from '../components/admin/TemplateList';
import type { PreviewChoice, PreviewDevice } from '../components/admin/TemplatePreviewPane';
import { TemplateWorkspace, type WorkspaceLayout } from '../components/admin/TemplateWorkspace';
import styles from './MessageTemplatesPage.module.css';

const PATH = '/admin/message-templates';
const NEW = 'new';
/** semantic.css's laptop and tablet widths: Write and Preview become tabs, then the list and the template take turns. */
const TABS_MEDIA = '(max-width: 1180px)';
const PAGE_MEDIA = '(max-width: 900px)';

/** The first template the list shows for a kind: system emails come grouped by who they go to. */
export function firstShown(rows: readonly TemplateRow[] | undefined, kind: TemplateKind): TemplateRow | undefined {
  const ofKind = (rows ?? []).filter((row) => row.kind === kind);
  if (kind === 'custom') return ofKind[0];
  return [...ofKind].sort((a, b) => TEMPLATE_AUDIENCES.indexOf(templateAudienceOf(a)) - TEMPLATE_AUDIENCES.indexOf(templateAudienceOf(b)))[0];
}

/** Somewhere the person was about to go while their words were unsaved. */
type Pending = { readonly anchor: AnchorRect; readonly go: () => void };

/**
 * ADMIN-09 (F15.08; Admin redesign batch 2, Option A "three panes: list · writing · the real email"): the wording of
 * every email the system sends, and the team's own templates for messages to candidates (Q12). Each save is a new kept
 * version; it changes what is sent from now on, never what has gone. `?template=` opens one (Overview's "Edit
 * templates" uses it). Unsaved words are never dropped without asking (06-qol §4), beside whatever was clicked.
 */
export function MessageTemplatesPage(): ReactNode {
  const canEdit = useCanEdit('hiring_setup');
  const router = useRouter();
  const raw = useSearch({ strict: false }) as Readonly<Record<string, unknown>>;
  const selectedId = typeof raw.template === 'string' && raw.template !== '' ? raw.template : null;
  const templates = useAdminTemplates(canEdit);
  const versions = useTemplateVersions(selectedId !== null && selectedId !== NEW ? selectedId : null);
  const isTabs = useMediaQuery(TABS_MEDIA);
  const isPage = useMediaQuery(PAGE_MEDIA);
  const [chosenKind, setChosenKind] = useState<TemplateKind>('custom');
  const [query, setQuery] = useState('');
  const [viewing, setViewing] = useState<TemplateVersionRow | null>(null);
  const [choice, setChoice] = useState<PreviewChoice | null>(null);
  const [device, setDevice] = useState<PreviewDevice>('desktop');
  const [isDirty, setIsDirty] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  const rows = templates.data;
  const current = selectedId === null || selectedId === NEW ? null : (rows?.find((row) => row.id === selectedId) ?? null);
  const kind: TemplateKind = selectedId === NEW ? 'custom' : (current?.kind ?? chosenKind);
  const layout: WorkspaceLayout = isPage ? 'page' : isTabs ? 'tabs' : 'split';

  // Leaving Admin's page (the menu, the browser's Back) with unsaved words asks first, in a bar on this page.
  const blocker = useBlocker({ shouldBlockFn: ({ current: from, next }) => isDirty && from.pathname !== next.pathname, enableBeforeUnload: () => isDirty, withResolver: true });

  const open = (id: string | null): void => {
    setViewing(null);
    setIsDirty(false);
    router.history.replace(`${PATH}${defaultStringifySearch(id ? { template: id } : {})}`);
  };
  /** Anything that would put the draft away asks first while it has unsaved words. */
  const guarded = (event: MouseEvent<HTMLElement>, go: () => void): void => {
    if (!isDirty) {
      go();
      return;
    }
    setPending({ anchor: anchorFrom(event.currentTarget), go });
  };

  // A wide screen always has a template open: the first of the kind shown, as the drawing has.
  const first = firstShown(rows, kind);
  useEffect(() => {
    if (layout !== 'page' && selectedId === null && first) router.history.replace(`${PATH}${defaultStringifySearch({ template: first.id })}`);
  }, [layout, selectedId, first, router]);

  if (!canEdit) return <ForbiddenState resource="message templates" contact="Your admin" />;
  if (templates.error && !rows) return <ServerErrorState requestId={templates.error.requestId} onRetry={() => void templates.refetch()} />;

  const isMissing = rows !== undefined && selectedId !== null && selectedId !== NEW && current === null;
  const showList = layout !== 'page' || selectedId === null;
  const showWork = layout !== 'page' || selectedId !== null;

  const workspace = isMissing ? (
    <div className={styles.missing}>
      <EmptyState isInset title="That template isn't here" body="It may have been opened from an old link. Choose one from the list." action={null} />
    </div>
  ) : selectedId === null ? null : selectedId === NEW || current ? (
    <TemplateWorkspace
      key={selectedId}
      template={current}
      viewing={viewing}
      layout={layout}
      choice={choice}
      device={device}
      onChoice={setChoice}
      onDevice={setDevice}
      onCloseVersion={() => setViewing(null)}
      onDirtyChange={setIsDirty}
      onSaved={(row: TemplateRow) => {
        if (row.id !== selectedId) open(row.id);
      }}
      onBack={(event) => guarded(event, () => open(null))}
    />
  ) : null;

  return (
    <div className={styles.page}>
      {blocker.status === 'blocked' && (
        <div className={styles.leaving} role="alert">
          <span>Your changes to {current?.name ?? 'the new template'} aren’t saved.</span>
          <Button variant="ghost" size="sm" onClick={blocker.reset}>
            Stay here
          </Button>
          <Button variant="dangerSolid" size="sm" onClick={blocker.proceed}>
            Leave without saving
          </Button>
        </div>
      )}
      <header className={styles.head}>
        <span className={styles.crumb}>Admin · Messages</span>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Message templates</h1>
          <span className={styles.grow} />
          <Button
            variant="solid"
            icon={FilePlus2}
            onClick={(event) =>
              guarded(event, () => {
                setChosenKind('custom');
                open(NEW);
              })
            }
          >
            New template
          </Button>
        </div>
        <p className={styles.lead}>The emails the system sends, and the team’s own templates for messages to candidates. A save becomes a new version; emails already sent never change.</p>
      </header>

      <div className={styles.layout} data-layout={layout}>
        {showList && (
          <TemplateList
            rows={rows}
            kind={kind}
            query={query}
            selectedId={selectedId}
            versions={versions.data}
            viewingVersion={viewing?.version ?? null}
            onKind={(next) => {
              if (next === kind) return;
              const go = (): void => {
                setChosenKind(next);
                open(null);
              };
              if (isDirty) setPending({ anchor: anchorFrom(document.activeElement instanceof HTMLElement ? document.activeElement : document.body), go });
              else go();
            }}
            onQuery={setQuery}
            onSelect={(row, event) => {
              if (row.id === selectedId) return;
              guarded(event, () => open(row.id));
            }}
            onVersion={setViewing}
          />
        )}
        {showWork && workspace}
      </div>

      <Popover anchor={pending?.anchor ?? null} onClose={() => setPending(null)} align="left" width={300} label="Leave without saving?">
        <div className={styles.confirm}>
          <h3 className={styles.confirmTitle}>Leave without saving?</h3>
          <p className={styles.confirmBody}>Your changes to {current?.name ?? 'the new template'} aren’t saved, and will be lost.</p>
          <div className={styles.confirmActions}>
            <Button variant="ghost" size="sm" onClick={() => setPending(null)}>
              Keep editing
            </Button>
            <Button
              variant="dangerSolid"
              size="sm"
              onClick={() => {
                const go = pending?.go;
                setPending(null);
                go?.();
              }}
            >
              Discard changes
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}
